import { useEffect, useMemo, useState } from 'react';
import copy from 'copy-to-clipboard';
import type { PopoverProps } from '@mui/material';
import { Typography } from '@mui/material';

import { useCurrentAccount } from 'src/hooks/useAccount';

import {
  CheckButton,
  CheckButtonContainer,
  CheckButtonIconContainer,
  CheckInstructionButton,
  Container,
  DescriptionContainer,
  IconContainer,
  MnemonicContainer,
  MnemonicWordContainer,
  MnemonicWordNumberContainer,
  MnemonicWordTextContainer,
  MnemonicWrapperContainer,
  SeedPhraseContainer,
  SeedPhraseTopContainer,
  StyledPopover,
  TopContainer,
  TopLeftContainer,
  ViewIconContainer,
  WarningContainer,
  WarningIconContainer,
  WrapperContainer,
} from './styled';
import GradientButton from '../GradientButton';
import IconButton from '../IconButton';
import { StyledTooltip } from '../WalletPopover/styled';

import { ReactComponent as CheckIcon } from 'src/assets/svg/Check.svg';
import { ReactComponent as Close32Icon } from 'src/assets/svg/Close32.svg';
import { ReactComponent as MnemonicWarningIcon } from 'src/assets/svg/MnemonicWarning.svg';
import { ReactComponent as ViewIcon } from 'src/assets/svg/View28.svg';
import { ReactComponent as ViewBlockIcon } from 'src/assets/svg/ViewBlock.svg';

type ConnectWalletPopoverProps = Omit<PopoverProps, 'children'>;

export default function MnemonicPopover({ onClose, ...remainder }: ConnectWalletPopoverProps) {
  const [isCheckedInstruction, setIsCheckedInstruction] = useState(false);
  const [isExport, setIsExport] = useState(false);
  const [isView, setIsView] = useState(false);

  const [isOpenTooltip, setIsOpenTooltip] = useState(false);

  const { currentAccount } = useCurrentAccount();

  const splitedMnemonic = useMemo(() => currentAccount.mnemonic.split(' '), [currentAccount.mnemonic]);

  const handleOnClickCopy = () => {
    if (copy(currentAccount.mnemonic)) {
      setIsOpenTooltip(true);
    }
  };

  useEffect(() => {
    if (isOpenTooltip) {
      setTimeout(() => {
        setIsOpenTooltip(false);
      }, 1000);
    }
  }, [isOpenTooltip]);

  return (
    <WrapperContainer data-is-open={remainder.open}>
      <StyledPopover
        onClose={() => {
          onClose?.({}, 'backdropClick');

          setIsCheckedInstruction(false);
          setIsExport(false);
          setIsView(false);
        }}
        {...remainder}
      >
        <Container>
          <TopContainer>
            <TopLeftContainer>
              <Typography variant="h2">{isExport ? 'Export Mnemonic' : 'Reveal Mnemonic'}</Typography>
            </TopLeftContainer>
            <IconButton
              onClick={() => {
                onClose?.({}, 'backdropClick');

                setIsCheckedInstruction(false);
                setIsExport(false);
                setIsView(false);
              }}
            >
              <IconContainer>
                <Close32Icon />
              </IconContainer>
            </IconButton>
          </TopContainer>
          <DescriptionContainer>Your mnemonic is a set of 24 words used to backup and access your account.</DescriptionContainer>

          {isExport ? (
            <SeedPhraseContainer>
              <SeedPhraseTopContainer>
                <Typography variant="h6">Seed Phrase</Typography>
                <IconButton
                  onClick={() => {
                    setIsView(!isView);
                  }}
                >
                  <ViewIconContainer>{isView ? <ViewIcon /> : <ViewBlockIcon />}</ViewIconContainer>
                </IconButton>
              </SeedPhraseTopContainer>

              <MnemonicWrapperContainer>
                <MnemonicContainer>
                  {splitedMnemonic.map((word, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <MnemonicWordContainer key={`${word}${index}`}>
                      <MnemonicWordNumberContainer>
                        <Typography variant="h8">{index + 1}</Typography>
                      </MnemonicWordNumberContainer>
                      <MnemonicWordTextContainer>
                        <Typography variant="h6">{isView ? word : '****'}</Typography>
                      </MnemonicWordTextContainer>
                    </MnemonicWordContainer>
                  ))}
                </MnemonicContainer>
              </MnemonicWrapperContainer>
            </SeedPhraseContainer>
          ) : (
            <>
              <WarningContainer>
                <WarningIconContainer>
                  <MnemonicWarningIcon />
                </WarningIconContainer>
                <Typography variant="h4">Anyone with your mnemonic has access to your wallet, putting your assets at risk.</Typography>
              </WarningContainer>

              <CheckInstructionButton
                onClick={() => {
                  setIsCheckedInstruction(!isCheckedInstruction);
                }}
              >
                <CheckButtonContainer>
                  <CheckButton>
                    {isCheckedInstruction ? (
                      <CheckButtonIconContainer>
                        <CheckIcon />
                      </CheckButtonIconContainer>
                    ) : null}
                  </CheckButton>
                </CheckButtonContainer>
                <Typography variant="h7">I understand the risks and I will never share my mnemonic with anyone.</Typography>
              </CheckInstructionButton>
            </>
          )}
          <StyledTooltip title="Copied!" placement="top" arrow open={isOpenTooltip}>
            <div>
              <GradientButton
                disabled={!isCheckedInstruction}
                onClick={() => {
                  if (isExport) {
                    handleOnClickCopy();
                  } else {
                    setIsExport(!isExport);
                  }
                }}
              >
                <Typography variant="h4">{isExport ? 'COPY' : 'REVEAL MNEMONIC'}</Typography>
              </GradientButton>
            </div>
          </StyledTooltip>
        </Container>
      </StyledPopover>
    </WrapperContainer>
  );
}
